import { FC, useCallback, useEffect, useMemo } from "react";
import Typography from "@mui/material/Typography";
import {
  LocalTopicsProvider,
  type TopicsState,
  useTopicsStateContext,
} from "@/lib/company-filter/modules/intent/hooks/useTopicsState";
import { TopicsSection } from "@/lib/company-filter/modules/intent/sections/TopicsSection";
import { useGetIntentTopicsByIdsQuery } from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import type { TopicsFilter } from "@/lib/scoring/types";
import type { CachedTopic } from "@/types/intent";

interface IntentTopicsSectionProps {
  topics: TopicsFilter;
  topicsCache: CachedTopic[];
  onChange: (topics: TopicsFilter, cache: CachedTopic[]) => void;
  showErrors?: boolean;
}

export const IntentTopicsSection: FC<IntentTopicsSectionProps> = ({
  topics,
  topicsCache,
  onChange,
  showErrors = false,
}) => {
  const topicError =
    showErrors && topics.ids.length === 0
      ? "You must select at least one topic"
      : showErrors && !topics.inclusionMethod
        ? "You must select whether to include 'any' or 'all' topics"
        : null;

  const handleStateChange = useCallback(
    (state: TopicsState) => {
      onChange(
        { ids: state.topicIds, inclusionMethod: state.topicsInclusionMethod },
        state.topicsCache
      );
    },
    [onChange]
  );

  const initialState = useMemo(
    () => ({
      topicIds: topics.ids,
      topicsCache,
      topicsInclusionMethod: topics.inclusionMethod ?? "ANY_PRESENT",
    }),
    [topics.ids, topicsCache, topics.inclusionMethod]
  );

  return (
    <LocalTopicsProvider
      initial={initialState}
      onStateChange={handleStateChange}
    >
      <TopicsCacheHydrator topicIds={topics.ids} />
      <TopicsSection hideWrapper />
      {topicError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: "block" }}
        >
          {topicError}
        </Typography>
      )}
    </LocalTopicsProvider>
  );
};

const TopicsCacheHydrator: FC<{ topicIds: string[] }> = ({ topicIds }) => {
  const ctx = useTopicsStateContext();
  const needsHydration =
    topicIds.length > 0 && (ctx?.topicsCache.length ?? 0) === 0;

  const { data } = useGetIntentTopicsByIdsQuery({
    variables: { ids: topicIds as [string, ...string[]] },
    skip: !needsHydration,
    context: { clientName: TAXONOMY_CLIENT },
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (!ctx || !data?.intentTopic?.data || !needsHydration) return;

    const hydrated: CachedTopic[] = data.intentTopic.data
      .filter(
        (t): t is NonNullable<typeof t> =>
          t !== null && t.id !== null && t.displayName !== null
      )
      .map((t) => ({
        id: t.id as string,
        displayName: t.displayName as string,
        category: t.category ?? undefined,
        collectionTree: (t.collectionTree ?? []).filter(
          (c): c is string => c !== null
        ),
      }));

    if (hydrated.length > 0) {
      ctx.update({ topicsCache: hydrated });
    }
  }, [ctx, data, needsHydration]);

  return null;
};
